import { PROPERTY_EXTRA_FIELDS } from "models/NotificationSettings";
import {
  ACTION_CREATE,
  ACTION_DELETE,
  ACTION_DETAILS,
  ACTION_GLOBAL_FILTER,
  ACTION_LIST,
  ACTION_TEST,
  ACTION_UPDATE,
  ACTIONS_CRU,
  ACTIONS_READ,
  ACTIONS_WRITE,
} from "models/role/action";
import {
  PROPERTY_ID,
  SUBJECT_ACCOUNT,
  SUBJECT_BANK,
  SUBJECT_CASH_MANAGEMENT,
  SUBJECT_CASH_MANAGEMENT_ACCOUNT,
  SUBJECT_CASH_MANAGEMENT_PROVIDER_SETUP,
  SUBJECT_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION,
  SUBJECT_CIRCUIT_BREAKER,
  SUBJECT_CLIENT,
  SUBJECT_CLIENT_VERIFY_NOTIFICATIONS,
  SUBJECT_CUSTOMER,
  SUBJECT_CUSTOMER_COUNTRIES,
  SUBJECT_CUSTOMER_PAYMENTS_PROVIDERS,
  SUBJECT_CUSTOMER_PRODUCT_FLAGS,
  SUBJECT_DYNAMIC_BENEFICIARY,
  SUBJECT_NOTIFICATION_SETTINGS,
  SUBJECT_PAY_BY_LINK,
  SUBJECT_PAYMENT,
  SUBJECT_PAYMENT_EXTERNAL_LINKS,
  SUBJECT_PAYMENT_NOTIFICATION,
  SUBJECT_PAYOUTS,
  SUBJECT_REFUND,
  SUBJECT_USER,
  SUBJECT_VERIFICATION,
  SUBJECT_VERIFY,
} from "models/role/subject";
import {
  PROPERTY_ACTIVE as CLIENT_PROPERTY_ACTIVE,
  PROPERTY_REDIRECT_SIGNATURE,
  PROPERTY_SECRET,
} from "modules/applications";
import {
  PROPERTY_PRODUCTION_ACCOUNT_BALANCE,
  PROPERTY_PRODUCTION_SETTLEMENT_BALANCE,
  PROPERTY_PRODUCTION_SETTLEMENT_BATCHES,
  PROPERTY_PRODUCTION_SETTLEMENT_SCHEDULE_CONFIG,
  PROPERTY_SANDBOX_ACCOUNT_BALANCE,
  PROPERTY_SANDBOX_SETTLEMENT_BALANCE,
  PROPERTY_SANDBOX_SETTLEMENT_BATCHES,
  PROPERTY_SANDBOX_SETTLEMENT_SCHEDULE_CONFIG,
} from "modules/cash-management";
import { PROPERTY_BRANDING_PREFERENCES } from "modules/checkout";
import {
  PROPERTY_ACTIVE as CUSTOMER_PROPERTY_ACTIVE,
  PROPERTY_ADDRESS,
  PROPERTY_CIRCUIT_BREAKER_ENABLED,
  PROPERTY_CREDENTIALS,
  PROPERTY_MERCHANT_CATEGORY_CODE,
  PROPERTY_PAYMENT_PREFERENCES,
  PROPERTY_PRODUCTION_GATEWAY,
  PROPERTY_PRODUCTION_PAY_BY_LINK,
  PROPERTY_PRODUCTION_PAYOUTS,
  PROPERTY_PRODUCTION_REFUND,
  PROPERTY_PRODUCTION_TRANSFORMER,
  PROPERTY_PRODUCTION_VERIFY,
  PROPERTY_SANDBOX_GATEWAY,
  PROPERTY_SANDBOX_PAY_BY_LINK,
  PROPERTY_SANDBOX_PAYOUTS,
  PROPERTY_SANDBOX_REFUND,
  PROPERTY_SANDBOX_TRANSFORMER,
  PROPERTY_SANDBOX_VERIFY,
  PROPERTY_TERMINATE_AT,
  PROPERTY_TYPE,
} from "modules/customers";
import {
  PROPERTY_BANK_RESPONSES,
  PROPERTY_BENEFICIARY_ACCOUNT_IDENTIFICATIONS,
  PROPERTY_PROVIDER,
  PROPERTY_SENDER,
  PROPERTY_SENDER_ACCOUNT_NAME,
  PROPERTY_SENDER_CHECKOUT,
  PROPERTY_SENDER_PAYMENT_PROVIDER,
  PROPERTY_SENDER_VOLT_CONNECT,
} from "modules/transactions";
import {
  PROPERTY_ACTIVE as USER_PROPERTY_ACTIVE,
  PROPERTY_VOLT_ADMIN,
} from "modules/user-management";
import { PROPERTY_COUNTRIES, PROPERTY_DATA_SCOPE } from "modules/verify";

import type { PERMISSION_GROUP_LIST_ALL } from "modules/auth";
import type { UUID } from "utils";
import type { AbilityInterface } from "./ability";

type permissionApplyFunction = (args: {
  ability: AbilityInterface;
  userId: UUID;
  customerId: UUID | null;
  hierarchyIds: UUID[];
}) => void;

const applyPermissions: Record<
  PERMISSION_GROUP_LIST_ALL,
  permissionApplyFunction
> = {
  PERMISSION_GROUP_CUSTOMER_VIEW: ({ ability, customerId, hierarchyIds }) => {
    ability.can(ACTION_LIST, SUBJECT_CUSTOMER);
    ability.can(
      ACTION_DETAILS,
      SUBJECT_CUSTOMER,
      [PROPERTY_ID, PROPERTY_ADDRESS],
      customerId
        ? {
            id: { $in: [customerId, ...hierarchyIds] },
          }
        : undefined
    );

    if (customerId) {
      ability.cannot(ACTION_UPDATE, SUBJECT_CUSTOMER, [
        CUSTOMER_PROPERTY_ACTIVE,
      ]);
    }
  },
  PERMISSION_VOLT_GROUP_CUSTOMER_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_CUSTOMER_VIEW(args);
  },
  PERMISSION_GROUP_CUSTOMER_BRANDING_PREFERENCES_MANAGE: ({
    ability,
    customerId,
    hierarchyIds,
  }) => {
    ability.can(
      [ACTION_CREATE, ACTION_UPDATE],
      SUBJECT_CUSTOMER,
      PROPERTY_BRANDING_PREFERENCES,
      customerId
        ? {
            id: { $in: [customerId, ...hierarchyIds] },
          }
        : undefined
    );
  },
  PERMISSION_VOLT_GROUP_CUSTOMER_BRANDING_PREFERENCES_MANAGE: (args) => {
    applyPermissions.PERMISSION_GROUP_CUSTOMER_BRANDING_PREFERENCES_MANAGE(
      args
    );
  },
  PERMISSION_GROUP_CUSTOMER_BRANDING_PREFERENCES_VIEW: ({
    ability,
    customerId,
    hierarchyIds,
  }) => {
    ability.can(
      ACTIONS_READ,
      SUBJECT_CUSTOMER,
      PROPERTY_BRANDING_PREFERENCES,
      customerId
        ? {
            id: { $in: [customerId, ...hierarchyIds] },
          }
        : undefined
    );
  },
  PERMISSION_VOLT_GROUP_CUSTOMER_BRANDING_PREFERENCES_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_CUSTOMER_BRANDING_PREFERENCES_VIEW(args);
  },
  PERMISSION_VOLT_GROUP_CUSTOMER_MANAGE: ({ ability }) => {
    ability.can([ACTION_UPDATE], SUBJECT_CUSTOMER, [
      PROPERTY_ID,
      CUSTOMER_PROPERTY_ACTIVE,
      PROPERTY_MERCHANT_CATEGORY_CODE,
      PROPERTY_ADDRESS,
      PROPERTY_TYPE,
    ]);
    ability.can([ACTION_CREATE, ACTION_UPDATE], SUBJECT_CUSTOMER_COUNTRIES);
    ability.can(ACTION_UPDATE, SUBJECT_CUSTOMER, [PROPERTY_TERMINATE_AT]);
  },
  PERMISSION_VOLT_GROUP_CUSTOMER_CREATION: ({ ability }) => {
    ability.can(ACTION_CREATE, SUBJECT_CUSTOMER, [PROPERTY_ID]);
  },
  PERMISSION_GROUP_CUSTOMER_CREDENTIALS_MANAGE: ({
    ability,
    customerId,
    hierarchyIds,
  }) => {
    ability.can(
      ACTION_CREATE,
      SUBJECT_CUSTOMER,
      PROPERTY_CREDENTIALS,
      customerId
        ? {
            id: { $in: [customerId, ...hierarchyIds] },
          }
        : undefined
    );
  },
  PERMISSION_VOLT_GROUP_CUSTOMER_CREDENTIALS_MANAGE: (args) => {
    applyPermissions.PERMISSION_GROUP_CUSTOMER_CREDENTIALS_MANAGE(args);
  },
  PERMISSION_GROUP_PRODUCT_ACTIVATION_MANAGE: ({ ability }) => {
    ability.can(ACTION_UPDATE, SUBJECT_CUSTOMER_PRODUCT_FLAGS, [
      PROPERTY_SANDBOX_VERIFY,
      PROPERTY_PRODUCTION_VERIFY,
    ]);
  },
  PERMISSION_VOLT_GROUP_PRODUCT_ACTIVATION_MANAGE: (args) => {
    applyPermissions.PERMISSION_GROUP_PRODUCT_ACTIVATION_MANAGE(args);
    args.ability.can(ACTION_UPDATE, SUBJECT_CUSTOMER, [
      PROPERTY_CIRCUIT_BREAKER_ENABLED,
    ]);
    args.ability.can(ACTION_UPDATE, SUBJECT_CUSTOMER_PRODUCT_FLAGS, [
      PROPERTY_SANDBOX_TRANSFORMER,
      PROPERTY_PRODUCTION_TRANSFORMER,
      PROPERTY_SANDBOX_REFUND,
      PROPERTY_PRODUCTION_REFUND,
      PROPERTY_PRODUCTION_PAY_BY_LINK,
      PROPERTY_SANDBOX_PAY_BY_LINK,
      PROPERTY_PRODUCTION_PAYOUTS,
      PROPERTY_SANDBOX_PAYOUTS,
      PROPERTY_PRODUCTION_GATEWAY,
      PROPERTY_SANDBOX_GATEWAY,
    ]);
  },
  PERMISSION_VOLT_GROUP_PAYMENT_PREFERENCES_UPDATE: ({ ability }) => {
    ability.can(ACTION_UPDATE, SUBJECT_CUSTOMER_PAYMENTS_PROVIDERS);

    ability.can([ACTION_UPDATE], SUBJECT_CUSTOMER, [
      PROPERTY_PAYMENT_PREFERENCES,
    ]);
  },
  PERMISSION_VOLT_GROUP_BANKS_MANAGE: ({ ability }) => {
    ability.can(ACTIONS_WRITE, SUBJECT_BANK, [PROPERTY_ID]);
  },
  PERMISSION_GROUP_BANKS_VIEW: () => void 0,
  PERMISSION_VOLT_GROUP_BANKS_VIEW: ({ ability }) => {
    ability.can(ACTIONS_READ, SUBJECT_BANK, [PROPERTY_ID]);
  },
  PERMISSION_GROUP_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION_SETUP: ({
    ability,
  }) => {
    ability.can(ACTIONS_CRU, SUBJECT_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION);
  },
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION_SETUP: (
    args
  ) => {
    applyPermissions.PERMISSION_GROUP_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION_SETUP(
      args
    );
    args.ability.can(
      ACTIONS_CRU,
      SUBJECT_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION
    );
  },
  PERMISSION_GROUP_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION_VIEW: ({
    ability,
  }) => {
    ability.can(ACTIONS_READ, SUBJECT_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION);
  },
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION_VIEW: (
    args
  ) => {
    applyPermissions.PERMISSION_GROUP_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION_VIEW(
      args
    );
  },
  PERMISSION_GROUP_CASH_MANAGEMENT_SETTINGS_VIEW: ({ ability }) => {
    ability.can(ACTIONS_READ, SUBJECT_CASH_MANAGEMENT);
    ability.can(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_ACCOUNT);
    ability.cannot(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
      PROPERTY_PRODUCTION_ACCOUNT_BALANCE,
      PROPERTY_SANDBOX_ACCOUNT_BALANCE,
    ]);
    ability.cannot(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
      PROPERTY_PRODUCTION_SETTLEMENT_SCHEDULE_CONFIG,
      PROPERTY_SANDBOX_SETTLEMENT_SCHEDULE_CONFIG,
    ]);
    ability.cannot(ACTIONS_CRU, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
      PROPERTY_PRODUCTION_SETTLEMENT_BALANCE,
      PROPERTY_SANDBOX_SETTLEMENT_BALANCE,
      PROPERTY_PRODUCTION_SETTLEMENT_SCHEDULE_CONFIG,
      PROPERTY_SANDBOX_SETTLEMENT_SCHEDULE_CONFIG,
    ]);
  },
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_SETTINGS_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_CASH_MANAGEMENT_SETTINGS_VIEW(args);
  },
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_SETTINGS_CONFIGURE: ({ ability }) => {
    ability.can(ACTIONS_CRU, SUBJECT_CASH_MANAGEMENT_ACCOUNT);
    ability.can(ACTIONS_CRU, SUBJECT_CASH_MANAGEMENT_PROVIDER_SETUP);
    ability.cannot(ACTIONS_WRITE, SUBJECT_CASH_MANAGEMENT, [
      PROPERTY_PRODUCTION_SETTLEMENT_BATCHES,
    ]);
    ability.cannot(ACTIONS_CRU, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
      PROPERTY_PRODUCTION_ACCOUNT_BALANCE,
      PROPERTY_SANDBOX_ACCOUNT_BALANCE,
      PROPERTY_PRODUCTION_SETTLEMENT_BALANCE,
      PROPERTY_SANDBOX_SETTLEMENT_BALANCE,
      PROPERTY_PRODUCTION_SETTLEMENT_SCHEDULE_CONFIG,
      PROPERTY_SANDBOX_SETTLEMENT_SCHEDULE_CONFIG,
    ]);
  },
  PERMISSION_GROUP_MANAGE_SETTLEMENT_BATCHES: ({ ability }) => {
    ability.can(ACTIONS_WRITE, SUBJECT_CASH_MANAGEMENT, [
      PROPERTY_SANDBOX_SETTLEMENT_BATCHES,
      PROPERTY_PRODUCTION_SETTLEMENT_BATCHES,
    ]);
  },
  PERMISSION_VOLT_GROUP_MANAGE_SETTLEMENT_BATCHES: ({ ability }) => {
    ability.can(ACTIONS_WRITE, SUBJECT_CASH_MANAGEMENT, [
      PROPERTY_SANDBOX_SETTLEMENT_BATCHES,
    ]);

    ability.cannot(ACTIONS_WRITE, SUBJECT_CASH_MANAGEMENT, [
      PROPERTY_PRODUCTION_SETTLEMENT_BATCHES,
    ]);
  },
  PERMISSION_GROUP_APPLICATIONS_MANAGE: ({ ability }) => {
    ability.can(ACTION_CREATE, SUBJECT_CLIENT, [PROPERTY_ID]);
    ability.can([ACTION_UPDATE, ACTION_DELETE], SUBJECT_CLIENT, [PROPERTY_ID]);
    ability.can([ACTION_DETAILS], SUBJECT_CLIENT, [PROPERTY_ID]);
    ability.can(ACTION_UPDATE, SUBJECT_CLIENT, [
      CLIENT_PROPERTY_ACTIVE,
      PROPERTY_SECRET,
      PROPERTY_REDIRECT_SIGNATURE,
    ]);
    ability.can(ACTION_TEST, SUBJECT_PAYMENT_NOTIFICATION);
    ability.can(ACTIONS_CRU, SUBJECT_NOTIFICATION_SETTINGS);
    ability.cannot(ACTION_UPDATE, SUBJECT_NOTIFICATION_SETTINGS, [
      PROPERTY_EXTRA_FIELDS,
    ]);
  },
  PERMISSION_VOLT_GROUP_APPLICATIONS_MANAGE: (args) => {
    applyPermissions.PERMISSION_GROUP_APPLICATIONS_MANAGE(args);
    const { ability } = args;

    ability.can(
      ACTION_UPDATE,
      SUBJECT_NOTIFICATION_SETTINGS,
      PROPERTY_EXTRA_FIELDS
    );
    ability.can([ACTION_DETAILS], SUBJECT_NOTIFICATION_SETTINGS, [
      PROPERTY_EXTRA_FIELDS,
    ]);
  },
  PERMISSION_GROUP_APPLICATIONS_VIEW: ({ ability }) => {
    ability.can([ACTION_LIST], SUBJECT_CLIENT);
    ability.can([ACTION_DETAILS], SUBJECT_CLIENT, [PROPERTY_ID]);
    ability.can([ACTION_DETAILS], SUBJECT_NOTIFICATION_SETTINGS, [
      PROPERTY_EXTRA_FIELDS,
    ]);
  },
  PERMISSION_VOLT_GROUP_APPLICATIONS_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_APPLICATIONS_VIEW(args);
  },
  PERMISSION_GROUP_CIRCUIT_BREAKER_MANAGE: ({ ability }) => {
    ability.can(ACTIONS_CRU, SUBJECT_CIRCUIT_BREAKER);
  },
  PERMISSION_GROUP_CIRCUIT_BREAKER_VIEW: ({ ability }) => {
    ability.can([ACTION_DETAILS, ACTION_LIST], SUBJECT_CIRCUIT_BREAKER);
  },
  PERMISSION_VOLT_GROUP_CIRCUIT_BREAKER_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_CIRCUIT_BREAKER_VIEW(args);
    args.ability.can(ACTION_GLOBAL_FILTER, SUBJECT_CIRCUIT_BREAKER);
  },
  PERMISSION_VOLT_GROUP_CIRCUIT_BREAKER_MANAGE: (args) => {
    applyPermissions.PERMISSION_GROUP_CIRCUIT_BREAKER_MANAGE(args);
    args.ability.can(ACTION_GLOBAL_FILTER, SUBJECT_CIRCUIT_BREAKER);
  },
  PERMISSION_GROUP_BANK_ACCOUNTS_VIEW: ({
    ability,
    customerId,
    hierarchyIds,
  }) => {
    ability.can(
      ACTIONS_READ,
      SUBJECT_ACCOUNT,
      PROPERTY_ID,
      customerId
        ? {
            customer: { $in: [customerId, ...hierarchyIds] },
          }
        : undefined
    );
  },
  PERMISSION_VOLT_GROUP_BANK_ACCOUNTS_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_BANK_ACCOUNTS_VIEW(args);
  },
  PERMISSION_GROUP_PAYMENTS_VIEW: ({ ability, customerId, hierarchyIds }) => {
    ability.can(ACTION_LIST, SUBJECT_PAYMENT);
    ability.can(
      ACTION_DETAILS,
      SUBJECT_PAYMENT,
      PROPERTY_ID,
      customerId
        ? {
            "beneficiary.customerId": { $in: [customerId, ...hierarchyIds] },
          }
        : undefined
    );
    ability.can(ACTION_DETAILS, SUBJECT_PAYMENT, PROPERTY_SENDER);
  },
  PERMISSION_VOLT_GROUP_PAYMENTS_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_PAYMENTS_VIEW(args);
    args.ability.can(ACTION_DETAILS, SUBJECT_PAYMENT, [
      PROPERTY_BANK_RESPONSES,
      PROPERTY_PROVIDER,
    ]);
    args.ability.can(ACTION_DETAILS, SUBJECT_PAYMENT_EXTERNAL_LINKS);
  },
  PERMISSION_GROUP_BENEFICIARY_VIEW: ({
    ability,
    customerId,
    hierarchyIds,
  }) => {
    ability.can(
      ACTION_DETAILS,
      SUBJECT_PAYMENT,
      PROPERTY_BENEFICIARY_ACCOUNT_IDENTIFICATIONS,
      customerId
        ? {
            "beneficiary.customerId": { $in: [customerId, ...hierarchyIds] },
          }
        : undefined
    );
  },
  PERMISSION_VOLT_GROUP_BENEFICIARY_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_BENEFICIARY_VIEW(args);
  },
  PERMISSION_VOLT_GROUP_BANK_ACCOUNTS_MANAGE: ({ ability }) => {
    ability.can(ACTIONS_READ, SUBJECT_ACCOUNT, [PROPERTY_ID]);
    ability.can(ACTIONS_WRITE, SUBJECT_ACCOUNT, [PROPERTY_ID]);
    ability.can(ACTIONS_WRITE, SUBJECT_DYNAMIC_BENEFICIARY, [PROPERTY_ID]);
  },
  PERMISSION_GROUP_SENSITIVE_DATA_VIEW: ({
    ability,
    customerId,
    hierarchyIds,
  }) => {
    ability.can(
      ACTION_DETAILS,
      SUBJECT_PAYMENT,
      PROPERTY_SENDER,
      customerId
        ? {
            "beneficiary.customerId": { $in: [customerId, ...hierarchyIds] },
          }
        : undefined
    );
    ability.can(ACTION_DETAILS, SUBJECT_PAYMENT, PROPERTY_SENDER_ACCOUNT_NAME);
    ability.can(ACTION_DETAILS, SUBJECT_PAYMENT, [
      PROPERTY_SENDER_VOLT_CONNECT,
      PROPERTY_SENDER_PAYMENT_PROVIDER,
    ]);
  },
  PERMISSION_VOLT_GROUP_SENSITIVE_DATA_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_SENSITIVE_DATA_VIEW(args);
    args.ability.can(ACTION_DETAILS, SUBJECT_PAYMENT, PROPERTY_SENDER_CHECKOUT);
  },
  PERMISSION_GROUP_USERS_VIEW: ({ ability, customerId, hierarchyIds }) => {
    ability.can(ACTION_LIST, SUBJECT_USER);
    ability.can(
      ACTION_DETAILS,
      SUBJECT_USER,
      PROPERTY_ID,
      customerId
        ? {
            customerId: { $in: [customerId, ...hierarchyIds] },
          }
        : undefined
    );
  },
  PERMISSION_VOLT_GROUP_USERS_VIEW: ({ ability, customerId, hierarchyIds }) => {
    ability.can(ACTION_LIST, SUBJECT_USER);
    ability.can(
      ACTION_DETAILS,
      SUBJECT_USER,
      PROPERTY_ID,
      customerId
        ? {
            customerId: { $in: [customerId, ...hierarchyIds] },
          }
        : undefined
    );
  },
  PERMISSION_GROUP_USERS_MANAGE: ({ ability, userId }) => {
    ability.can(ACTION_CREATE, SUBJECT_USER, PROPERTY_ID);
    ability.can(ACTION_UPDATE, SUBJECT_USER, [
      PROPERTY_ID,
      USER_PROPERTY_ACTIVE,
    ]);
    ability.cannot(ACTION_UPDATE, SUBJECT_USER, USER_PROPERTY_ACTIVE, {
      id: userId,
    });
    ability.can(ACTIONS_READ, SUBJECT_USER, PROPERTY_ID);
  },
  PERMISSION_VOLT_GROUP_USERS_MANAGE: ({ ability, userId }) => {
    ability.can(ACTION_CREATE, SUBJECT_USER, PROPERTY_ID);
    ability.can(ACTION_UPDATE, SUBJECT_USER, [
      PROPERTY_ID,
      USER_PROPERTY_ACTIVE,
    ]);
    ability.cannot(ACTION_UPDATE, SUBJECT_USER, USER_PROPERTY_ACTIVE, {
      id: userId,
    });
    ability.can(ACTIONS_READ, SUBJECT_USER, PROPERTY_ID);
    ability.can(ACTION_CREATE, SUBJECT_USER, PROPERTY_VOLT_ADMIN);
  },
  PERMISSION_GROUP_REFUNDS_AND_PAYBACKS_VIEW: ({ ability }) => {
    ability.can(ACTION_DETAILS, SUBJECT_REFUND);
    ability.can(ACTION_LIST, SUBJECT_REFUND);
  },
  PERMISSION_VOLT_GROUP_REFUNDS_AND_PAYBACKS_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_REFUNDS_AND_PAYBACKS_VIEW(args);
  },
  PERMISSION_GROUP_CASH_MANAGEMENT_MANAGE_REFUND_AND_PAYBACKS: ({
    ability,
  }) => {
    ability.can(ACTION_CREATE, SUBJECT_REFUND);
  },
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_MANAGE_REFUND_AND_PAYBACKS: ({
    ability,
  }) => {
    // Manage refunds for volt user is only for user-mgmt purposes. Volt cannot manage refunds
    ability.cannot(ACTION_CREATE, SUBJECT_REFUND);
  },
  PERMISSION_GROUP_VERIFICATION_VIEW: ({ ability }) => {
    ability.can(ACTIONS_READ, SUBJECT_VERIFICATION, PROPERTY_ID);
  },
  PERMISSION_VOLT_GROUP_VERIFICATION_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_VERIFICATION_VIEW(args);
  },
  PERMISSION_GROUP_AIS_NOTIFICATION_VIEW: ({ ability }) => {
    ability.can(ACTIONS_READ, SUBJECT_CLIENT_VERIFY_NOTIFICATIONS);
  },
  PERMISSION_VOLT_GROUP_AIS_NOTIFICATION_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_AIS_NOTIFICATION_VIEW(args);
  },
  PERMISSION_GROUP_AIS_NOTIFICATION_MANAGE: ({ ability }) => {
    ability.can(ACTIONS_WRITE, SUBJECT_CLIENT_VERIFY_NOTIFICATIONS);
  },
  PERMISSION_VOLT_GROUP_AIS_NOTIFICATION_MANAGE: (args) => {
    applyPermissions.PERMISSION_GROUP_AIS_NOTIFICATION_MANAGE(args);
  },
  PERMISSION_VOLT_GROUP_AIS_SCOPE_MANAGE: ({ ability }) => {
    ability.can(ACTIONS_CRU, SUBJECT_VERIFY, PROPERTY_DATA_SCOPE);
  },
  PERMISSION_GROUP_AIS_VIEW: ({ ability }) => {
    ability.can(ACTIONS_READ, SUBJECT_VERIFY, PROPERTY_COUNTRIES);
  },
  PERMISSION_VOLT_GROUP_AIS_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_AIS_VIEW(args);
  },
  PERMISSION_GROUP_AIS_MANAGE: ({ ability }) => {
    ability.can(ACTIONS_WRITE, SUBJECT_VERIFY, PROPERTY_COUNTRIES);
  },
  PERMISSION_VOLT_GROUP_AIS_MANAGE: (args) => {
    applyPermissions.PERMISSION_GROUP_AIS_MANAGE(args);
  },
  PERMISSION_VOLT_GROUP_PROVIDER_BANK_MANAGE: () => void 0,
  PERMISSION_VOLT_GROUP_TRAFFIC_RELAY_CONFIG: () => void 0,
  PERMISSION_GROUP_CASH_MANAGEMENT_ACCOUNT_BALANCE_VIEW: ({ ability }) => {
    ability.can(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
      PROPERTY_PRODUCTION_ACCOUNT_BALANCE,
      PROPERTY_SANDBOX_ACCOUNT_BALANCE,
    ]);
  },
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_ACCOUNT_BALANCE_VIEW: ({ ability }) => {
    ability.can(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
      PROPERTY_PRODUCTION_ACCOUNT_BALANCE,
      PROPERTY_SANDBOX_ACCOUNT_BALANCE,
    ]);
  },
  PERMISSION_GROUP_CASH_MANAGEMENT_SETTLEMENT_BALANCE_VIEW: ({ ability }) => {
    ability.can(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
      PROPERTY_PRODUCTION_SETTLEMENT_BALANCE,
      PROPERTY_SANDBOX_SETTLEMENT_BALANCE,
    ]);
  },
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_SETTLEMENT_BALANCE_VIEW: ({
    ability,
  }) => {
    ability.can(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
      PROPERTY_PRODUCTION_SETTLEMENT_BALANCE,
      PROPERTY_SANDBOX_SETTLEMENT_BALANCE,
    ]);
  },
  PERMISSION_GROUP_PAY_BY_LINK_MANAGE: ({ ability }) => {
    ability.can(ACTIONS_WRITE, SUBJECT_PAY_BY_LINK);
  },
  PERMISSION_VOLT_GROUP_PAY_BY_LINK_MANAGE: (args) => {
    applyPermissions.PERMISSION_GROUP_PAY_BY_LINK_MANAGE(args);
  },
  PERMISSION_GROUP_CASH_MANAGEMENT_PAYOUTS_VIEW: ({ ability }) => {
    ability.can(ACTION_LIST, SUBJECT_PAYOUTS);
  },
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_PAYOUTS_VIEW: (args) => {
    applyPermissions.PERMISSION_GROUP_CASH_MANAGEMENT_PAYOUTS_VIEW(args);
  },
  PERMISSION_VOLT_GROUP_LATAM_SETTLEMENTS_MANAGE: () => void 0,
  PERMISSION_VOLT_GROUP_LATAM_REFUNDS_MANAGE: () => void 0,
  PERMISSION_VOLT_GROUP_FEATURE_FLAG_VIEW: () => void 0,
  PERMISSION_VOLT_GROUP_FEATURE_FLAG_MANAGE: () => void 0,
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_APPROVE_PAYOUTS: () => void 0,
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_MANAGE_PAYOUTS: () => void 0,
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_PAYOUT_NOTIFICATION_SETTINGS_VIEW: () =>
    void 0,
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_PAYOUT_NOTIFICATION_SETTINGS_MANAGE: () =>
    void 0,
  PERMISSION_GROUP_CASH_MANAGEMENT_APPROVE_PAYOUTS: () => void 0,

  PERMISSION_GROUP_CASH_MANAGEMENT_MANAGE_PAYOUTS: () => void 0,
  PERMISSION_GROUP_CASH_MANAGEMENT_PAYOUT_NOTIFICATION_SETTINGS_VIEW: () =>
    void 0,
  PERMISSION_GROUP_CASH_MANAGEMENT_PAYOUT_NOTIFICATION_SETTINGS_MANAGE: () =>
    void 0,
  PERMISSION_GROUP_CASH_MANAGEMENT_SETTLEMENT_SCHEDULE_CONFIGURATION_MANAGE: () =>
    void 0,
  PERMISSION_GROUP_CASH_MANAGEMENT_SETTLEMENT_SCHEDULE_CONFIGURATION_VIEW: ({
    ability,
  }) => {
    ability.can(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
      PROPERTY_PRODUCTION_SETTLEMENT_SCHEDULE_CONFIG,
      PROPERTY_SANDBOX_SETTLEMENT_SCHEDULE_CONFIG,
    ]);
  },
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_SETTLEMENT_SCHEDULE_CONFIGURATION_MANAGE: () =>
    void 0,
  PERMISSION_VOLT_GROUP_CASH_MANAGEMENT_SETTLEMENT_SCHEDULE_CONFIGURATION_VIEW: ({
    ability,
  }) => {
    ability.can(ACTION_DETAILS, SUBJECT_CASH_MANAGEMENT_ACCOUNT, [
      PROPERTY_PRODUCTION_SETTLEMENT_SCHEDULE_CONFIG,
      PROPERTY_SANDBOX_SETTLEMENT_SCHEDULE_CONFIG,
    ]);
  },
  PERMISSION_GROUP_SETTLEMENT_BATCHES_VIEW: () => void 0,
  PERMISSION_VOLT_GROUP_SETTLEMENT_BATCHES_VIEW: () => void 0,
  PERMISSION_VOLT_GROUP_FEATURE_FLAG_CREATE: () => void 0,
};

export default applyPermissions;
